import { OperationVariables } from '@apollo/client';
import { useEffect, useMemo } from 'react';

import { BaseOption, SelectValueType } from 'ui/selects';
import { BaseOption as BaseOptionV2 } from 'ui/selectv2/types';
import { useLazyGraphql } from 'utils/hooks/useLazyGraphql';

import { baseQueryArgs } from './constants';
import { FilterSelectSuffix } from './PaginatedSelect.styles';
import { GqlQuery, ItemResponse } from './types';

function defaultParser<TValue extends SelectValueType = string>(
  data: unknown,
): BaseOption<TValue>[] {
  if (!Array.isArray(data)) {
    if (process.env.NODE_ENV !== 'production') {
      /* istanbul ignore next */

      console.warn(
        'Unable to process results in useAvailableOptions.  Please check the `parseResponse` parameter.',
      );
    }
    return [];
  }

  return (data as ItemResponse[]).map(({ id, name }) => ({
    id,
    label: name,
    value: `${id}` as unknown as TValue,
    Suffix: <FilterSelectSuffix>{id}</FilterSelectSuffix>,
  }));
}

export type OptionsParser<TValue extends SelectValueType = string> = (
  data: unknown,
) => BaseOption<TValue>[];

export type AvailableOptionsProps<TValue extends SelectValueType = string> = {
  /** Query to fetch multiple items */
  query: GqlQuery;
  /** The initial variables to be used when fetching the items */
  variables?: OperationVariables;
  /** Get the list of options from the `fetchItemsQuery` response */
  parseResponse?: OptionsParser<TValue>;
};

export function useAvailableOptions<TValue extends SelectValueType = string>({
  query,
  variables,
  parseResponse = defaultParser,
}: AvailableOptionsProps<TValue>) {
  const { fetch, data, error, isLoading } = useLazyGraphql(query, baseQueryArgs);

  useEffect(() => {
    fetch(variables);
  }, [JSON.stringify(variables)]);

  const results = useMemo(() => parseResponse(data), [data]);

  return {
    results,
    error,
    isLoading,
  };
}

// New Select

function defaultParserV2<TValue extends SelectValueType = string>(
  data: unknown,
): BaseOption<TValue>[] {
  if (!Array.isArray(data)) {
    if (process.env.NODE_ENV !== 'production') {
      /* istanbul ignore next */

      console.warn(
        'Unable to process results in useAvailableOptions.  Please check the `parseResponse` parameter.',
      );
    }
    return [];
  }

  return (data as ItemResponse[]).map(({ id, name }) => ({
    id,
    label: name,
    value: `${id}` as unknown as TValue,
    Suffix: <FilterSelectSuffix>{id}</FilterSelectSuffix>,
  }));
}

export type OptionsParserV2<TValue extends SelectValueType = string> = (
  data: unknown,
) => BaseOptionV2<TValue>[];

export type AvailableOptionsPropsV2<TValue extends SelectValueType = string> = {
  /** Query to fetch multiple items */
  query: GqlQuery;
  /** The initial variables to be used when fetching the items */
  variables?: OperationVariables;
  /** Get the list of options from the `fetchItemsQuery` response */
  parseResponse?: OptionsParserV2<TValue>;
  skip?: boolean;
};

export function useAvailableOptionsV2<TValue extends SelectValueType = string>({
  query,
  variables,
  skip = false,
  parseResponse = defaultParserV2,
}: AvailableOptionsPropsV2<TValue>) {
  const { fetch, data, error, isLoading } = useLazyGraphql(query, baseQueryArgs);

  const variablesString = JSON.stringify(variables);

  useEffect(() => {
    if (!skip) fetch(variables);
  }, [variablesString, skip]);

  const results = parseResponse(data);

  return {
    results,
    error,
    isLoading,
  };
}
